import * as React from 'react';
import { makeStyles, Tooltip, Link } from '@fluentui/react-components';
import { UserMessageV2 as UserMessage } from '@fluentui-copilot/react-copilot-chat';
import { Copy20Regular, Copy20Filled, Edit20Regular, Edit20Filled } from '@fluentui/react-icons';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'error' | 'separator';
  content: string;
  timestamp?: number;
  source?: string;
  intent?: string;
  showNewConversation?: boolean;
  separator?: string;
  from?: string;
  meta?: {
    sources?: Array<{ title?: string; url?: string; content?: string; score?: number }>;
    confidence?: number;
    processingTime?: number;
    tableHtml?: string;
    tableNote?: string;
    formFields?: any[];
    formTitle?: string;
    formDescription?: string;
    originalError?: Error | string;
  };
}

interface CustomUserMessageProps {
  message: Message;
  onPromptSelected?: (prompt: string) => void;
  children: React.ReactNode;
}

const useStyles = makeStyles({
  copyIconButton: {
    paddingLeft: "10px",
    paddingRight: "10px",        
    position: "relative",
    zIndex: 99999, // Add z-index
    "& .regularIcon": {
      display: "block",
      width: "20px !important",    
      minWidth: "20px",
      maxWidth: "20px",
      color: "#0078d4",
    },
    "& .filledIcon": {
      display: "none",
      color: "#0078d4",
    },
    "&:hover .regularIcon": {
      display: "none",
    },
    "&:hover .filledIcon": {
      display: "block",
      width: "20px !important", 
      minWidth: "20px",
      maxWidth: "20px",           
    },
  },
  editIconButton: {  
    paddingLeft: "10px",
    paddingRight: "10px",      
    position: "relative",
    zIndex: 99999, // Add z-index
    "& .editRegularIcon": {
      display: "block",
      width: "20px",
      minWidth: "20px",
      maxWidth: "20px",
      color: "#0078d4",
    },
    "& .editFilledIcon": {
      display: "none",
      color: "#0078d4",
    },
    "&:hover .editRegularIcon": {
      display: "none",
    },
    "&:hover .editFilledIcon": {
      display: "block",
      width: "20px",
      minWidth: "20px",
      maxWidth: "20px",
    },
  },
  actionBar: {
    height: "32px",
    display: "flex",
    alignItems: "center",
    width: "fit-content",
    zIndex: 99999, // Add z-index
  },
  UserMessage: {
    wordBreak: "break-word",
    zIndex:1
  },
});

const CustomUserMessage: React.FC<CustomUserMessageProps> = ({ message, onPromptSelected, children }) => {
  const styles = useStyles();
  const [tooltipText, setTooltipText] = React.useState("Copy");
  const [tooltipTextEdit, setTooltipTextEdit] = React.useState("Edit");
  const [isActionBarVisible, setIsActionBarVisible] = React.useState(false);
  const actionBarRef = React.useRef<HTMLDivElement>(null);

  const handleCopyClick = async () => {
    let success = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(message.content);
        success = true;
      }
    } catch {
      // Fall through to fallback
    }
    if (!success) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = message.content;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        success = document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch {
        success = false;
      }
    }
    if (success) {
      setTooltipText("Copied");
      const ariaLiveRegion = document.getElementById('global-aria-live');
      if (ariaLiveRegion) {
        ariaLiveRegion.textContent = 'User message copied to clipboard';
        setTimeout(() => { ariaLiveRegion.textContent = ''; }, 3000);
      }
    } else {
      console.error('Failed to copy text to clipboard');
    }
    setTimeout(() => {
      setTooltipText("Copy");
    }, 10000);
  };

  const handleEditClick = () => {
    if (onPromptSelected) {
      onPromptSelected(message.content);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (actionBarRef.current && !actionBarRef.current.contains(event.target as Node)) {
      setIsActionBarVisible(false);
    }
  };

  const handleVisibilityOnFocus = () => {
    setIsActionBarVisible(true);
  };

  const handleBlur = (event: React.FocusEvent) => {
    setIsActionBarVisible(false);
    // Check if the blur event is related to a child element
    if (
      actionBarRef.current &&
      !actionBarRef.current.contains(event.relatedTarget as Node)
    ) {
      setIsActionBarVisible(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Tab") {
      if ((event.target as HTMLElement).id === "customUserMessage") {
        setIsActionBarVisible(true);
      } else {
        setIsActionBarVisible(false);
      }
    }
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("focus", handleVisibilityOnFocus);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("focus", handleVisibilityOnFocus);
    };
  }, []);

  return (
    <UserMessage
      tabIndex={0}
      id="customUserMessage"
      ref={actionBarRef}
      onMouseEnter={() => setIsActionBarVisible(true)}
      onMouseLeave={() => setIsActionBarVisible(false)}
      onKeyUp={handleKeyDown}
      onBlur={handleBlur}
      className={styles.UserMessage}
      actionBar={isActionBarVisible && (
        <div className={styles.actionBar}>
          <Tooltip content={tooltipTextEdit} relationship="label" withArrow>
            <Link
              tabIndex={0}
              id="editIconButton"
              aria-label="Edit"
              className={styles.editIconButton}
              appearance="subtle"
              onClick={handleEditClick}
              style={{visibility: isActionBarVisible ? "visible" : "hidden"}}
            >
              <Edit20Regular className="editRegularIcon" />
              <Edit20Filled className="editFilledIcon" />
            </Link>
          </Tooltip>
          <Tooltip content={tooltipText} relationship="label" withArrow>
            <Link
              tabIndex={0}
              aria-label="Copy"
              className={styles.copyIconButton}
              appearance="subtle"
              onClick={handleCopyClick}
              style={{visibility: isActionBarVisible ? "visible" : "hidden"}}
            >
              <Copy20Regular className="regularIcon" />
              <Copy20Filled className="filledIcon" />
            </Link>
          </Tooltip>
        </div>
      )}
    >
      {children}
    </UserMessage>
  );
};

export default CustomUserMessage;